import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, ChevronRight, Database, Lock, Eye, Globe, User } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function PrivacyPolicy() {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Database,
      title: language === 'es' ? 'Información que Recopilamos' : 'Information We Collect',
      items: [
        {
          subtitle: language === 'es' ? 'Información Personal' : 'Personal Information',
          content: language === 'es'
            ? 'Nombre, dirección de correo electrónico, número de teléfono, información de pago...'
            : 'Name, email address, phone number, payment information...',
        },
        {
          subtitle: language === 'es' ? 'Información de Transacciones' : 'Transaction Information',
          content: language === 'es'
            ? 'Montos de transacción, comerciantes, beneficiarios, historial de autorizaciones...'
            : 'Transaction amounts, merchants, beneficiaries, authorization history...',
        },
        {
          subtitle: language === 'es' ? 'Información del Dispositivo' : 'Device Information',
          content: language === 'es'
            ? 'Dirección IP, tipo de navegador, sistema operativo, identificadores de dispositivo...'
            : 'IP address, browser type, operating system, device identifiers...',
        },
      ],
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      icon: Eye,
      title: language === 'es' ? 'Cómo Usamos su Información' : 'How We Use Your Information',
      items: [
        {
          subtitle: language === 'es' ? 'Proporcionar Servicios' : 'Provide Services',
          content: language === 'es'
            ? 'Para procesar sus transacciones y proporcionar soporte al cliente...'
            : 'To process your transactions and provide customer support...',
        },
        {
          subtitle: language === 'es' ? 'Seguridad y Prevención de Fraude' : 'Security & Fraud Prevention',
          content: language === 'es'
            ? 'Para detectar y prevenir actividades fraudulentas y proteger su cuenta...'
            : 'To detect and prevent fraudulent activity and protect your account...',
        },
        {
          subtitle: language === 'es' ? 'Cumplimiento Legal' : 'Legal Compliance',
          content: language === 'es'
            ? 'Para cumplir con obligaciones legales y regulatorias, incluyendo requisitos KYC/AML...'
            : 'To comply with legal and regulatory obligations, including KYC/AML requirements...',
        },
      ],
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      icon: User,
      title: language === 'es' ? 'Compartir Información' : 'Information Sharing',
      items: [
        {
          subtitle: language === 'es' ? 'Proveedores de Servicios' : 'Service Providers',
          content: language === 'es'
            ? 'Compartimos información con terceros que nos ayudan a operar nuestros servicios (procesadores de pago, verificación de identidad)...'
            : 'We share information with third parties who help us operate our services (payment processors, identity verification)...',
        },
        {
          subtitle: language === 'es' ? 'Requisitos Legales' : 'Legal Requirements',
          content: language === 'es'
            ? 'Podemos divulgar información cuando sea requerido por ley o para proteger nuestros derechos legales...'
            : 'We may disclose information when required by law or to protect our legal rights...',
        },
      ],
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      icon: Lock,
      title: language === 'es' ? 'Seguridad de Datos' : 'Data Security',
      items: [
        {
          subtitle: language === 'es' ? 'Encriptación' : 'Encryption',
          content: language === 'es'
            ? 'Todos los datos se encriptan en tránsito (TLS) y en reposo...'
            : 'All data is encrypted in transit (TLS) and at rest...',
        },
        {
          subtitle: language === 'es' ? 'Control de Acceso' : 'Access Control',
          content: language === 'es'
            ? 'El acceso a datos personales está restringido a empleados que lo necesitan para sus funciones laborales...'
            : 'Access to personal data is restricted to employees who need it for their job functions...',
        },
      ],
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      icon: Globe,
      title: language === 'es' ? 'Transferencias Internacionales' : 'International Transfers',
      items: [
        {
          subtitle: language === 'es' ? 'Ubicación de Datos' : 'Data Location',
          content: language === 'es'
            ? 'Sus datos pueden transferirse y almacenarse en servidores ubicados fuera de su país de residencia...'
            : 'Your data may be transferred to and stored on servers located outside your country of residence...',
        },
        {
          subtitle: language === 'es' ? 'Salvaguardias' : 'Safeguards',
          content: language === 'es'
            ? 'Implementamos salvaguardias apropiadas para proteger sus datos durante las transferencias internacionales...'
            : 'We implement appropriate safeguards to protect your data during international transfers...',
        },
      ],
      status: 'LEGAL REVIEW REQUIRED',
    },
  ];

  const rights = [
    {
      right: language === 'es' ? 'Acceso' : 'Access',
      description: language === 'es' 
        ? 'Solicitar acceso a su información personal' 
        : 'Request access to your personal information',
    },
    {
      right: language === 'es' ? 'Corrección' : 'Correction',
      description: language === 'es' 
        ? 'Solicitar la corrección de información inexacta' 
        : 'Request correction of inaccurate information',
    },
    {
      right: language === 'es' ? 'Eliminación' : 'Deletion',
      description: language === 'es' 
        ? 'Solicitar la eliminación de su información (sujeto a requisitos legales)' 
        : 'Request deletion of your information (subject to legal requirements)',
    },
    {
      right: language === 'es' ? 'Portabilidad' : 'Portability',
      description: language === 'es' 
        ? 'Recibir una copia de sus datos en un formato estructurado' 
        : 'Receive a copy of your data in a structured format',
    },
    {
      right: language === 'es' ? 'Oposición' : 'Objection',
      description: language === 'es' 
        ? 'Oponerse al procesamiento de sus datos para ciertos propósitos' 
        : 'Object to processing of your data for certain purposes',
    },
  ];

  return (
    <Layout>
      <SEO
        title={language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
        description={language === 'es' 
          ? 'Cómo PeCashGlobal recopila, usa y protege su información personal.'
          : 'How PeCashGlobal collects, uses, and protects your personal information.'}
        canonical="https://pecashglobal.com/privacy"
        lang={language}
      />
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div {...fadeInUp} className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-full text-sm mb-6">
                <AlertTriangle className="w-4 h-4" />
                {language === 'es' ? 'Borrador - Revisión Legal Pendiente' : 'Draft - Legal Review Pending'}
              </div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-full mb-6">
                <Shield className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
              </h1>
              <p className="text-slate-400">
                {language === 'es' ? 'Última actualización: [PENDIENTE]' : 'Last Updated: [PENDING]'}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          {/* Legal Review Notice */}
          <motion.div 
            {...fadeInUp}
            className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">
                  {language === 'es' ? 'Aviso Importante' : 'Important Notice'}
                </h3>
                <p className="text-amber-800 text-sm">
                  {language === 'es' 
                    ? 'Este documento es un borrador y requiere revisión legal para cumplir con CCPA, GDPR y otras regulaciones de privacidad aplicables. Las secciones contienen texto de marcador de posición.'
                    : 'This document is a draft and requires legal review for CCPA, GDPR, and other applicable privacy regulations compliance. Sections contain placeholder text.'}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Introduction */}
          <motion.div {...fadeInUp} className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <p className="text-slate-600 leading-relaxed">
              {language === 'es'
                ? 'PeCashGlobal ("nosotros", "nuestro") se compromete a proteger su privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos su información cuando utiliza nuestros servicios.'
                : 'PeCashGlobal ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.'}
            </p>
          </motion.div>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.section
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl border border-slate-200 p-6"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-xl font-semibold text-slate-900">
                        {section.title}
                      </h2>
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                        {section.status}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 ml-14">
                  {section.items.map((item, i) => (
                    <div key={i}>
                      <h3 className="font-medium text-slate-900 mb-1">{item.subtitle}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{item.content}</p>
                    </div>
                  ))}
                </div>
              </motion.section>
            ))}
          </div>

          {/* Your Rights */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl border border-slate-200 p-6 mt-8"
          >
            <h2 className="text-xl font-semibold text-slate-900 mb-6">
              {language === 'es' ? 'Sus Derechos' : 'Your Rights'}
            </h2>
            <p className="text-sm text-amber-600 mb-4">[LEGAL REVIEW REQUIRED: Rights may vary by jurisdiction]</p>
            <div className="grid md:grid-cols-2 gap-4">
              {rights.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2" />
                  <div>
                    <span className="font-medium text-slate-900">{item.right}: </span>
                    <span className="text-slate-600 text-sm">{item.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Contact */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mt-8"
          >
            <h2 className="text-xl font-semibold text-emerald-900 mb-4">
              {language === 'es' ? 'Contáctenos' : 'Contact Us'}
            </h2>
            <p className="text-emerald-800">
              {language === 'es' 
                ? 'Para preguntas sobre esta política de privacidad o para ejercer sus derechos, contáctenos en:'
                : 'For questions about this privacy policy or to exercise your rights, contact us at:'}
            </p>
            <p className="text-emerald-900 font-medium mt-2">support@pecashglobal.com</p>
          </motion.section>

          {/* Navigation */}
          <motion.div 
            {...fadeInUp}
            className="mt-12 flex flex-wrap gap-4 justify-center"
          >
            <Link 
              to="/terms"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-colors"
            >
              {language === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
              <ChevronRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/compliance"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 border border-slate-200 rounded-full hover:bg-slate-50 transition-colors"
            >
              {language === 'es' ? 'Cumplimiento' : 'Compliance'}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
